import React, { useState } from 'react';
import { FiX, FiCopy, FiMail, FiLink } from 'react-icons/fi';

const InviteUserModal = ({ isOpen, onClose, token, onInvite }) => {
  const [inviteType, setInviteType] = useState('link');
  const [formData, setFormData] = useState({
    email: '',
    role: 'PM',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteResult, setInviteResult] = useState(null);

  if (!isOpen) return null;

  const handleDirectInvite = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        setInviteResult({ type: 'direct', data });
        onInvite();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to invite user');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLink = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invites/team-member`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          role: formData.role,
          expiresInDays: 7
        })
      });

      if (response.ok) {
        const data = await response.json();
        const fullUrl = `${window.location.origin}${data.inviteUrl}`;
        setInviteResult({ type: 'link', data: { ...data, fullUrl } });
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to create invitation link');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ email: '', role: 'PM', message: '' });
    setInviteResult(null);
    setError('');
    setInviteType('link');
    onClose();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 className="modal-title">Invite Team Member</h2>
          <button className="modal-close" onClick={handleClose}>
            <FiX size={20} />
          </button>
        </div>
        
        {inviteResult ? (
          <div className="modal-body">
            {inviteResult.type === 'link' ? (
              <>
                <div style={{ 
                  padding: '20px', 
                  backgroundColor: '#e8f5e9', 
                  borderRadius: '8px', 
                  marginBottom: '16px',
                  border: '1px solid #1DBF73'
                }}>
                  <h3 style={{ color: '#1DBF73', marginBottom: '12px', fontSize: '16px' }}>✓ Invitation Link Created!</h3>
                  <p style={{ fontSize: '14px' }}>Share this link with your team member. Valid for 7 days.</p>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Invitation Link</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      className="input"
                      value={inviteResult.data.fullUrl}
                      readOnly
                      data-testid="invite-link"
                    />
                    <button 
                      type="button" 
                      className="btn btn-primary"
                      onClick={() => copyToClipboard(inviteResult.data.fullUrl)}
                    >
                      <FiCopy size={16} />
                    </button>
                  </div>
                  <p className="form-helper">Role: <strong>{formData.role}</strong></p>
                </div>
              </>
            ) : (
              <>
                <div style={{ 
                  padding: '20px', 
                  backgroundColor: '#e8f5e9', 
                  borderRadius: '8px', 
                  marginBottom: '16px',
                  border: '1px solid #1DBF73'
                }}>
                  <h3 style={{ color: '#1DBF73', marginBottom: '12px', fontSize: '16px' }}>✓ User Created Successfully!</h3>
                  <p style={{ marginBottom: '12px', fontSize: '14px' }}>User <strong>{inviteResult.data.user.email}</strong> has been added.</p>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Temporary Password</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      className="input"
                      value={inviteResult.data.temporaryPassword}
                      readOnly
                      data-testid="temp-password"
                    />
                    <button 
                      type="button" 
                      className="btn btn-primary"
                      onClick={() => copyToClipboard(inviteResult.data.temporaryPassword)}
                    >
                      <FiCopy size={16} />
                    </button>
                  </div>
                  <p className="form-helper">Share this password with {inviteResult.data.user.email}</p>
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginBottom: '24px',
              padding: '0 32px'
            }}>
              <button
                type="button"
                className={`invite-type-btn ${inviteType === 'link' ? 'active' : ''}`}
                onClick={() => setInviteType('link')}
                data-testid="link-invite-option"
              >
                <FiLink size={18} />
                <span>Create Invite Link</span>
              </button>
              <button
                type="button"
                className={`invite-type-btn ${inviteType === 'direct' ? 'active' : ''}`}
                onClick={() => setInviteType('direct')}
                data-testid="direct-invite-option"
              >
                <FiMail size={18} />
                <span>Create User Directly</span>
              </button>
            </div>
            
            <form onSubmit={inviteType === 'link' ? handleCreateLink : handleDirectInvite}>
              <div className="modal-body">
                {inviteType === 'direct' && (
                  <div className="form-group">
                    <label className="form-label">Email Address *</label>
                    <input
                      type="email"
                      className="input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required={inviteType === 'direct'}
                      placeholder="user@example.com"
                      data-testid="invite-email-input"
                    />
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Role *</label>
                  <select
                    className="input"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    data-testid="invite-role-select"
                  >
                    <option value="PM">Project Manager</option>
                    <option value="Site Engineer">Site Engineer</option>
                    <option value="Designer">Designer</option>
                    <option value="Client">Client</option>
                  </select>
                </div>
                {inviteType === 'link' && (
                  <p className="form-helper" style={{ marginTop: '-8px' }}>
                    Anyone with the link can register with this role. Link expires in 7 days.
                  </p>
                )}
                {error && <div className="form-error">{error}</div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={loading}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading} data-testid="submit-invite">
                  {loading ? 'Processing...' : (inviteType === 'link' ? 'Generate Invite Link' : 'Create User')}
                </button>
              </div>
            </form>
          </>
        )}
        
        {inviteResult && (
          <div className="modal-footer">
            <button className="btn btn-primary" onClick={handleClose}>
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteUserModal;